import { FC, useEffect, useState } from "react";
import styled from "styled-components";
import { useQuery } from "@tanstack/react-query";

import { CloseButton, StyledDialog } from "../AlertDialogs/Dialog";
import { Modal } from "../Modal";
import Text from "../Text";
import ExportScreen from "./ExportScreen";
import PresetFormScreen from "./PresetFormScreen";
import { api } from "@/utils/api";
import ContentSpinner from "../ContentSpinner";
import { ExportPresetResource } from "@/types/resources";
import PresetsManagementScreen from "./PresetsManagementScreen";

const Dialog = styled(StyledDialog)`
  width: 800px;
  padding: 0;
`;

const Header = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
  height: 72px;
  align-items: center;
  padding: 0 24px;
`;

export const Footer = styled.div`
  box-shadow: 0px -1px 8px rgba(34, 34, 34, 0.06);
  height: 112px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 24px;
  gap: 16px;
`;

export const ScreenHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 0 24px;
  height: 63px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

export enum ExportGroup {
  YOUTUBE_TEST_RESULTS = "youtube-tests-results",
}

export enum Screens {
  EXPORT,
  PRESET_FORM,
  PRESETS_MANAGEMENT,
}

interface Props {
  primaryTitle: string;
  secondaryTitle: string;
  exportGroup: ExportGroup;
  onClose: () => void;
  onExport: (cols: string[]) => Promise<string>;
  fileName?: string;
}

const CsvExportModal: FC<Props> = ({
  primaryTitle,
  secondaryTitle,
  exportGroup,
  onClose,
  onExport,
  fileName = "export.csv",
}) => {
  const [currentScreen, setCurrentScreen] = useState(Screens.EXPORT);
  const [exportables, setExportables] = useState<string[]>([]);
  const [editablePreset, setEditablePreset] =
    useState<ExportPresetResource | null>(null);

  const { data: columns, isLoading } = useQuery(
    ["csv_export_columns", { key: exportGroup }],
    () =>
      api.get<never, string[]>("/v1/csv-export-columns", {
        params: {
          key: exportGroup,
        },
      }),
    {
      staleTime: 120 * 1000,
    }
  );

  const { data: presets } = useQuery(
    ["export_presets", { group: exportGroup }],
    () =>
      api.get<never, ExportPresetResource[]>("/v1/export-presets", {
        params: {
          group: exportGroup,
        },
      }),
    {
      staleTime: 120 * 1000,
    }
  );

  useEffect(() => {
    if (!columns) return;

    setExportables(columns);
  }, [columns]);

  const renderCurrentScreen = () => {
    switch (currentScreen) {
      case Screens.EXPORT:
        return (
          <ExportScreen
            onCancel={onClose}
            setExportables={setExportables}
            fileName={fileName}
            onExport={onExport}
            setCurrentScreen={setCurrentScreen}
            columns={columns!}
            exportables={exportables}
            presets={presets!}
          />
        );
      case Screens.PRESET_FORM:
        return (
          <PresetFormScreen
            exportGroup={exportGroup}
            editablePreset={editablePreset}
            exportables={exportables}
            onBack={() => {
              setCurrentScreen(
                editablePreset ? Screens.PRESETS_MANAGEMENT : Screens.EXPORT
              );
              setEditablePreset(null);
            }}
            columns={columns!}
            afterSubmitCallback={(preset) => {
              setCurrentScreen(Screens.EXPORT);
              setEditablePreset(null);
              setExportables(preset.exportables);
            }}
          />
        );
      case Screens.PRESETS_MANAGEMENT:
        return (
          <PresetsManagementScreen
            presets={presets!}
            onPresetEdit={(preset) => {
              setEditablePreset(preset);
              setCurrentScreen(Screens.PRESET_FORM);
            }}
            exportGroup={exportGroup}
            setCurrentScreen={setCurrentScreen}
          />
        );
      default:
        return null;
    }
  };

  const renderHeader = () => {
    switch (currentScreen) {
      case Screens.EXPORT:
        return (
          <>
            <Text as="span" $color="gray" $type="button">
              {primaryTitle}
            </Text>
            <Text as="span" $type="button">
              &nbsp;·&nbsp;{secondaryTitle}
            </Text>
          </>
        );
      case Screens.PRESET_FORM:
        return (
          <Text as="span" $type="button">
            {editablePreset ? "Edit Preset" : "Create Preset"}
          </Text>
        );
      case Screens.PRESETS_MANAGEMENT:
        return (
          <Text as="span" $type="button">
            Manage Presets
          </Text>
        );
      default:
        return null;
    }
  };

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <Header>{renderHeader()}</Header>
        <CloseButton onClick={onClose} type="button">
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
        {isLoading ? <ContentSpinner /> : renderCurrentScreen()}
      </Dialog>
    </Modal>
  );
};

export default CsvExportModal;
